/**
 * @fileoverview Detalhe de um curso da Área de Treinamento (consume). Player do
 * vídeo selecionado (YouTube/Vimeo), módulos em accordion com aulas ordenadas,
 * progresso geral + "Continue assistindo", marcar/desmarcar concluída. Gate por
 * `enableAcademy` no mount + listener de socket.
 */

import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  LinearProgress,
  Button,
  Chip,
} from "@material-ui/core";
import { CheckCircle2, Circle, PlayCircle, ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import useAcademy from "../../hooks/useAcademy";
import useSettings from "../../hooks/useSettings";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles(theme => {
  const dark = theme.palette.type === "dark";
  return {
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(3),
      flexWrap: "wrap",
      gap: theme.spacing(2),
    },
    title: {
      fontSize: "1.75rem",
      fontWeight: 700,
      color: dark ? "#FFFFFF" : "#1e293b",
    },
    // Timeline à ESQUERDA (360px), player à direita
    layout: {
      display: "grid",
      gridTemplateColumns: "360px minmax(0, 1fr)",
      gap: theme.spacing(2),
      padding: 0,
      alignItems: "start",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    main: {
      minWidth: 0,
      // no mobile o player vem antes da timeline
      [theme.breakpoints.down("sm")]: {
        order: -1,
      },
    },
    playerWrap: {
      width: "100%",
      aspectRatio: "16 / 9",
      backgroundColor: "#000",
      borderRadius: theme.spacing(1),
      overflow: "hidden",
      position: "relative",
    },
    playerPlaceholder: {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    },
    lessonHeader: {
      marginTop: theme.spacing(2),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    playerLessonTitle: {
      fontSize: "1.1rem",
      fontWeight: 600,
      color: dark ? "#f8fafc" : "#1e293b",
      margin: 0,
    },
    playerLessonDesc: {
      fontSize: "0.875rem",
      marginTop: theme.spacing(0.5),
      color: dark ? "rgba(248,250,252,0.7)" : "#64748b",
    },
    headerBar: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      flexWrap: "wrap",
      width: "100%",
    },
    progressWrap: {
      flex: 1,
      minWidth: 180,
    },
    progress: {
      height: 8,
      borderRadius: 4,
      marginTop: theme.spacing(0.5),
    },
    // ===== Timeline (conteúdo do curso) =====
    sidebar: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    sidebarEyebrow: {
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.45)" : "#94a3b8",
    },
    sidebarProgress: {
      fontSize: "0.95rem",
      fontWeight: 600,
      color: dark ? "#e5e7eb" : "#1e293b",
      marginBottom: theme.spacing(0.5),
    },
    progressNum: {
      color: "#4f7cff",
      fontWeight: 700,
    },
    moduleCard: {
      border: dark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(15,23,42,0.08)",
      borderRadius: 12,
      padding: theme.spacing(1),
      background: dark ? "rgba(255,255,255,0.02)" : "#ffffff",
    },
    moduleHeader: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.5, 0.5, 1, 0.5),
    },
    moduleBadge: {
      width: 26,
      height: 26,
      flexShrink: 0,
      borderRadius: 8,
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      fontSize: "0.8rem",
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    moduleName: {
      flex: 1,
      minWidth: 0,
      fontWeight: 600,
      fontSize: "0.9rem",
      color: dark ? "#f8fafc" : "#1e293b",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    moduleCount: {
      flexShrink: 0,
      fontSize: "0.7rem",
      fontWeight: 700,
      color: dark ? "rgba(255,255,255,0.55)" : "#64748b",
      background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      borderRadius: 6,
      padding: "2px 8px",
    },
    lessonList: {
      display: "flex",
      flexDirection: "column",
    },
    lessonRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      borderRadius: 8,
      cursor: "pointer",
      borderLeft: "3px solid transparent",
      transition: "background 0.15s ease",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(61,92,253,0.04)",
      },
    },
    lessonActive: {
      background: dark ? "rgba(79,124,255,0.12)" : "rgba(61,92,253,0.08)",
      borderLeft: "3px solid #4f7cff",
    },
    checkDone: { color: "#22c55e", flexShrink: 0 },
    checkTodo: { color: dark ? "rgba(255,255,255,0.35)" : "#cbd5e1", flexShrink: 0 },
    lessonTitle: {
      flex: 1,
      minWidth: 0,
      fontSize: "0.82rem",
      color: dark ? "#e5e7eb" : "#334155",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    lessonTag: {
      flexShrink: 0,
      fontSize: "0.6rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: "#4f7cff",
      background: dark ? "rgba(79,124,255,0.15)" : "rgba(61,92,253,0.1)",
      borderRadius: 4,
      padding: "2px 6px",
    },
  };
});

const Course = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const { showCourse, markComplete, unmarkComplete } = useAcademy();
  const { getAll } = useSettings();

  const [course, setCourse] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const redirectDisabled = useCallback(() => {
    toast.info(i18n.t("academy.toasts.disabled"));
    history.push("/");
  }, [history]);

  const allLessons = course
    ? (course.modules || []).flatMap(m => m.lessons || [])
    : [];
  const selectedLesson =
    allLessons.find(l => l.id === selectedId) || allLessons[0] || null;

  const fetchCourse = useCallback(
    async () => {
      const data = await showCourse(id);
      setCourse(data);
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const settings = await getAll();
        const academySetting = Array.isArray(settings)
          ? settings.find(s => s.key === "enableAcademy")
          : null;
        if (academySetting?.value === "disabled") {
          if (active) redirectDisabled();
          return;
        }
        const data = await fetchCourse();
        const firstId =
          data.continueLessonId ||
          (data.modules || []).flatMap(m => m.lessons || [])[0]?.id ||
          null;
        if (active) setSelectedId(firstId);
      } catch (err) {
        toastError(err);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!user?.companyId) return;
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);
    const handler = data => {
      if (
        data.action === "update" &&
        data.setting?.key === "enableAcademy" &&
        data.setting?.value === "disabled"
      ) {
        redirectDisabled();
      }
    };
    socket.on(`company-${companyId}-settings`, handler);
    return () => {
      socket.off(`company-${companyId}-settings`, handler);
    };
  }, [socketManager, user?.companyId, redirectDisabled]);

  const handleToggleComplete = async () => {
    if (!selectedLesson) return;
    try {
      if (selectedLesson.completed) {
        await unmarkComplete(selectedLesson.id);
      } else {
        await markComplete(selectedLesson.id);
      }
      await fetchCourse();
    } catch (err) {
      toastError(err);
    }
  };

  const buildEmbedUrl = lesson => {
    if (!lesson || !lesson.videoId) return null;
    if (lesson.provider === "vimeo") {
      const hash = lesson.videoHash ? `?h=${lesson.videoHash}` : "";
      return `https://player.vimeo.com/video/${lesson.videoId}${hash}`;
    }
    return `https://www.youtube.com/embed/${lesson.videoId}`;
  };

  const total = course?.lessonsCount || 0;
  const completed = course?.completedCount || 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const allDone = total > 0 && course?.continueLessonId == null;
  const embedUrl = buildEmbedUrl(selectedLesson);

  const goToContinue = () => {
    if (course?.continueLessonId) setSelectedId(course.continueLessonId);
  };

  return (
    <MainContainer className={classes.root}>
      <div className={classes.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Button
            size="small"
            startIcon={<ArrowLeft size={16} />}
            onClick={() => history.push("/academy")}
          >
            {i18n.t("academy.back")}
          </Button>
          <Typography className={classes.title}>{course?.title || ""}</Typography>
        </div>
      </div>

      <div className={classes.headerBar} style={{ marginBottom: 16 }}>
        <div className={classes.progressWrap}>
          <Typography variant="caption" color="textSecondary">
            {completed}/{total} · {pct}% {i18n.t("academy.progress")}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            className={classes.progress}
          />
        </div>
        {allDone ? (
          <Chip
            color="primary"
            icon={<CheckCircle2 size={16} />}
            label={i18n.t("academy.courseCompleted")}
          />
        ) : (
          course?.continueLessonId && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayCircle size={18} />}
              onClick={goToContinue}
            >
              {i18n.t("academy.continueWatching")}
            </Button>
          )
        )}
      </div>

      <div className={classes.layout}>
        <div className={classes.sidebar}>
          <Typography className={classes.sidebarEyebrow}>
            {i18n.t("academy.courseContent")}
          </Typography>
          <Typography className={classes.sidebarProgress}>
            <span className={classes.progressNum}>{completed}</span>{" "}
            {i18n.t("academy.ofLessonsCompleted", { total })}
          </Typography>

          {(course?.modules || []).map((module, mi) => {
            const lessons = module.lessons || [];
            const done = lessons.filter(l => l.completed).length;
            return (
              <div key={module.id} className={classes.moduleCard}>
                <div className={classes.moduleHeader}>
                  <span className={classes.moduleBadge}>{mi + 1}</span>
                  <Typography className={classes.moduleName}>
                    {module.title}
                  </Typography>
                  <span className={classes.moduleCount}>
                    {done}/{lessons.length}
                  </span>
                </div>
                <div className={classes.lessonList}>
                  {lessons.map(lesson => (
                    <div
                      key={lesson.id}
                      className={`${classes.lessonRow} ${
                        lesson.id === selectedLesson?.id ? classes.lessonActive : ""
                      }`}
                      onClick={() => setSelectedId(lesson.id)}
                    >
                      {lesson.completed ? (
                        <CheckCircle2 size={18} className={classes.checkDone} />
                      ) : (
                        <Circle size={18} className={classes.checkTodo} />
                      )}
                      <span className={classes.lessonTitle}>{lesson.title}</span>
                      <span className={classes.lessonTag}>
                        {i18n.t("academy.lessonTag")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className={classes.main}>
          <div className={classes.playerWrap}>
            {embedUrl ? (
              <iframe
                title={selectedLesson?.title || "player"}
                src={embedUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  border: 0,
                }}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className={classes.playerPlaceholder}>
                <Typography>{i18n.t("academy.videoUnavailable")}</Typography>
              </div>
            )}
          </div>

          {selectedLesson && (
            <div className={classes.lessonHeader}>
              <div>
                <Typography className={classes.playerLessonTitle}>
                  {selectedLesson.title}
                </Typography>
                {selectedLesson.description && (
                  <Typography className={classes.playerLessonDesc}>
                    {selectedLesson.description}
                  </Typography>
                )}
              </div>
              <Button
                variant={selectedLesson.completed ? "outlined" : "contained"}
                color="primary"
                startIcon={
                  selectedLesson.completed ? (
                    <Circle size={18} />
                  ) : (
                    <CheckCircle2 size={18} />
                  )
                }
                onClick={handleToggleComplete}
              >
                {selectedLesson.completed
                  ? i18n.t("academy.unmarkComplete")
                  : i18n.t("academy.markComplete")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default Course;
